import React, { FC, ReactNode, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Project } from 'api/project';
import { RoutePaths } from 'constant';
import { getSelectedTasks } from 'data/sprints/selectors';
import { logger } from 'services';
import cx from './index.module.css';
import {
  useKanbanState,
  UseNonPaginatedKanbanStateParam,
  BaseUseKanbanStateParam,
  UsePaginatedKanbanStateParam,
} from './kanban-state';
import { Swimlane } from './KanbanComponents';
import { KanbanContext, KanbanContextParams } from './KanbanContext';
import {
  debounceScrollFetch,
  fetchMemoryBuffer,
  inMemoryBuffer,
  minimumSwimlaneHeight,
  minimumSwimlaneHeightForOneSwimlane,
} from './knob';
import {
  Id,
  NewTasksDefaultParamsGetterCallbackType,
  PaginatedSwimlaneColumnFetch,
  PaginatedSwimlaneFetch,
  SwimlaneFetchParams,
  TaskCardRendererProps,
  UpdateSwimlaneParams,
} from './type';
import { getKanbanFetchStatus } from './utils';
import { VirtualizedList } from './VirtualizedList';

interface BaseKanbanBoardProps<GenericTask extends { id: Id }>
  extends BaseUseKanbanStateParam<GenericTask> {
  taskCardRenderer: FC<TaskCardRendererProps<GenericTask>>;
  taskCardLoadingRenderer: FC<TaskCardRendererProps>;
  children?: ReactNode;
  onKanbanReady: (api: KanbanContextParams['kanbanActions']) => void;
  height: number;
  getNewTaskParams: NewTasksDefaultParamsGetterCallbackType;
  isColumnHidden: boolean;
  statusesToShow?: number[];
}

export interface PaginatedKanbanBoardProps<GenericTask extends { id: Id }>
  extends UsePaginatedKanbanStateParam<GenericTask>,
    BaseKanbanBoardProps<GenericTask> {
  swimlaneFetch: PaginatedSwimlaneFetch<GenericTask>;
  swimlaneColumnFetch: PaginatedSwimlaneColumnFetch<GenericTask>;
  initialswimlaneFetchForAddedTasks?: () => Promise<
    UpdateSwimlaneParams<GenericTask>
  >;
}

interface NonPaginatedKanbanBoardProps<GenericTask extends { id: Id }>
  extends UseNonPaginatedKanbanStateParam<GenericTask>,
    BaseKanbanBoardProps<GenericTask> {
  swimlaneFetch?: never;
  swimlaneColumnFetch?: never;
  initialswimlaneFetchForAddedTasks?: never;
}

type KanbanBoardProps<GenericTask extends { id: Id }> =
  | NonPaginatedKanbanBoardProps<GenericTask>
  | PaginatedKanbanBoardProps<GenericTask>;

export const KanbanBoard = <GenericTask extends { id: Id }>({
  handleDragAndDrop,
  swimlaneFetch,
  swimlaneColumnFetch,
  taskCardRenderer,
  height: kanbanHeight,
  layoutFetch,
  taskCardLoadingRenderer,
  onKanbanReady,
  isPaginated,
  getNewTaskParams,
  isColumnHidden,
  projectDetail,
  statusesToShow,
  initialswimlaneFetchForAddedTasks,
}: KanbanBoardProps<GenericTask> & {
  projectDetail?: Project;
}): JSX.Element => {
  const { kanbanState, kanbanActions, drag, setDrag, swimlanesRef } =
    useKanbanState<GenericTask>({
      handleDragAndDrop,
      layoutFetch: layoutFetch as any,
      isPaginated,
    });
  const kanbanFetchStatus = getKanbanFetchStatus(kanbanState);
  const kanbanFetchStatusRef = useRef(kanbanFetchStatus);

  useEffect(() => {
    kanbanFetchStatusRef.current = kanbanFetchStatus;
  }, [kanbanFetchStatus]);

  useEffect(() => {
    if (statusesToShow) {
      kanbanActions.filterStatuses({ statusesToShow });
    }
  }, [statusesToShow, kanbanActions]);

  const swimlaneIds = kanbanState.orderSwimlaneIds;
  const dragRef = useRef();

  const swimlaneFetcher = useCallback(
    async (
      params: SwimlaneFetchParams,
    ): Promise<UpdateSwimlaneParams<GenericTask> | undefined> => {
      if (!isPaginated) {
        return undefined;
      }
      logger.debug('fetching swimlanes', params.swimlaneIds);

      const idsToBeFetched = params.swimlaneIds.filter((id) =>
        kanbanFetchStatusRef.current[id]
          ? kanbanFetchStatusRef.current[id].networkState === 'idle'
          : true,
      );

      if (idsToBeFetched.length === 0) {
        return undefined;
      }
      kanbanActions.updateSwimlaneRequestStatus({
        status: 'loading',
        swimlaneIds: idsToBeFetched,
      });

      const promise = swimlaneFetch({
        ...params,
        swimlaneIds: idsToBeFetched,
      });

      promise
        .then((res) => {
          kanbanActions.updateSwimlaneRequestStatus({
            status: 'success',
            swimlaneIds: idsToBeFetched,
          });
          kanbanActions.updatePaginatedSwimlane(res);

          return res;
        })
        .catch(() => {
          kanbanActions.updateSwimlaneRequestStatus({
            status: 'failed',
            swimlaneIds: idsToBeFetched,
          });
        });

      return promise;
    },
    [isPaginated, kanbanActions, swimlaneFetch],
  );

  const initialSwimlaneFetcher = useCallback(async (): Promise<
    UpdateSwimlaneParams<GenericTask> | undefined
  > => {
    if (!isPaginated) {
      return undefined;
    }
    logger.debug('fetching swimlanes for sprint');
    const promise = initialswimlaneFetchForAddedTasks?.();

    promise
      ?.then((res) => {
        kanbanActions.updateSwimlaneForSprint(res);

        return res;
      })
      .catch(() => {});

    return promise;
  }, [isPaginated, kanbanActions, initialswimlaneFetchForAddedTasks]);
  const numberOfSwimlanes = isColumnHidden
    ? swimlaneIds.filter((id) => kanbanState.data[id].count).length
    : swimlaneIds.length;

  const adjustedMinimumSwimlaneHeight =
    numberOfSwimlanes > 1
      ? minimumSwimlaneHeight
      : minimumSwimlaneHeightForOneSwimlane;
  const swimlaneHeight =
    kanbanHeight / numberOfSwimlanes <= adjustedMinimumSwimlaneHeight
      ? adjustedMinimumSwimlaneHeight
      : kanbanHeight / numberOfSwimlanes;

  const selectedTasks = useSelector(getSelectedTasks);
  const { pathname } = useLocation();
  const isSprint = pathname.includes(RoutePaths.sprintPlanning);

  return (
    <KanbanContext.Provider
      value={{
        dragTask: drag,
        setTask: setDrag,
        handleDragAndDrop,
        kanbanState,
        kanbanActions,
        dragRef,
      }}
    >
      <div
        className={cx.parent}
        onDragEndCapture={() => {
          setDrag(null);
        }}
      >
        <VirtualizedList
          name="swimlanes"
          innerRef={swimlanesRef}
          numItems={numberOfSwimlanes}
          itemHeight={swimlaneHeight}
          parentHeight={kanbanHeight - 4}
          debounceTimer={debounceScrollFetch}
          onScroll={useCallback(
            async (start, end) => {
              const inMemStart =
                start - inMemoryBuffer > 0 ? start - inMemoryBuffer : 0;
              const inMemEnd =
                end + inMemoryBuffer < numberOfSwimlanes
                  ? end + inMemoryBuffer
                  : numberOfSwimlanes;
              const swimlanesInViewIds = swimlaneIds.slice(
                inMemStart,
                inMemEnd + 1,
              );

              const fetchInMemStart =
                start - fetchMemoryBuffer > 0 ? start - fetchMemoryBuffer : 0;
              const fetchInMemEnd =
                end + fetchMemoryBuffer < numberOfSwimlanes
                  ? end + fetchMemoryBuffer
                  : numberOfSwimlanes;

              const fetchSwimlanesInViewIds = swimlaneIds.slice(
                fetchInMemStart,
                fetchInMemEnd + 1,
              );

              await swimlaneFetcher({
                swimlaneIds: fetchSwimlanesInViewIds,
                endOffset: end,
                startOffset: start,
              });
              kanbanActions.purgeData({
                inView: {
                  swimlaneIds: swimlanesInViewIds,
                },
                endOffset: 10,
                startOffset: 0,
              });
            },
            [kanbanActions, numberOfSwimlanes, swimlaneFetcher, swimlaneIds],
          )}
          onMount={useCallback(
            async (start, end) => {
              if (end === -1 || Number.isNaN(end)) {
                onKanbanReady(kanbanActions);

                return;
              }
              requestAnimationFrame(async () => {
                const inMemStart =
                  start - inMemoryBuffer > 0 ? start - inMemoryBuffer : 0;
                const inMemEnd =
                  end + inMemoryBuffer < swimlaneIds.length
                    ? end + inMemoryBuffer
                    : swimlaneIds.length;
                const swimlanesInViewIds = swimlaneIds.slice(
                  inMemStart,
                  inMemEnd + 1,
                );

                await swimlaneFetcher({
                  swimlaneIds: swimlanesInViewIds,
                  endOffset: end,
                  startOffset: start,
                });
                /* Had to do this since, for sprints the added tasks are not available immediately in the kanban api.
                So this is a hacky way to show the tasks immediately. */
                if (
                  selectedTasks.length > 0 &&
                  isSprint &&
                  initialswimlaneFetchForAddedTasks
                ) {
                  await initialSwimlaneFetcher();
                }
              });
            },
            [
              kanbanActions,
              onKanbanReady,
              swimlaneFetcher,
              swimlaneIds,
              initialSwimlaneFetcher,
              selectedTasks,
              isSprint,
              initialswimlaneFetchForAddedTasks,
            ],
          )}
          renderItem={({ index, style }) => (
            <div style={style} key={swimlaneIds[index]}>
              <Swimlane
                swimlaneId={swimlaneIds[index]}
                kanbanState={kanbanState}
                key={swimlaneIds[index]}
                taskCardRenderer={taskCardRenderer}
                taskCardLoadingRenderer={taskCardLoadingRenderer}
                swimlaneColumnFetch={swimlaneColumnFetch}
                swimlaneHeight={swimlaneHeight}
                getNewTaskParams={getNewTaskParams}
                projectDetail={projectDetail}
                areColumnsFilteredBasedOnStatuses={!!statusesToShow}
              />
            </div>
          )}
        />
      </div>
    </KanbanContext.Provider>
  );
};
